/**
 * useCurrentProfile — single source of truth for the caller's profile + roles.
 *
 * Both `AuthContext` (via `AuthProvider`) and `MFAGuard` will consume this
 * hook. RTK Query's cache keyed on `profileId` means parallel callers
 * coalesce into one network trip — the root cause of the duplicate
 * `vw_react_user_profiles_min` + `user_roles` calls seen on boot.
 */
import { useGetMyProfileAndRolesQuery } from "../api/authApi";
import type { UserProfile } from "../types/authTypes";

interface UseCurrentProfileArgs {
  profileId: string | null;
}

interface UseCurrentProfileResult {
  profile: UserProfile | null;
  aal: "aal1" | "aal2" | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useCurrentProfile({
  profileId,
}: UseCurrentProfileArgs): UseCurrentProfileResult {
  const query = useGetMyProfileAndRolesQuery(
    { profileId: profileId ?? "" },
    { skip: !profileId },
  );

  const error = query.error
    ? ((query.error as { message?: string }).message ?? "Profile load failed")
    : null;

  return {
    profile: query.data?.profile ?? null,
    aal: query.data?.aal ?? null,
    loading: query.isLoading,
    error,
    refetch: () => {
      void query.refetch();
    },
  };
}
